import type { TFunction } from 'i18next'

import type { ClientActivity, GuestActivityValue } from '@/api/clients/get-client-activities'
import { EMPTY_VALUE } from '@/constants/ui'
import { formatDateOnly } from '@/utils/date'
import { getUserRoleLabel } from '@/utils/user'

const DATE_FIELDS = ['startTime', 'endTime']
const RESIDENT_ROLE = 'Client'
const FRONT_DESK_ROLE = 'ShiftManager'

// Note: unknown fields (a backend addition) fall back to the raw key rather than disappearing.
const getFieldLabel = (field: string, t: TFunction<['clients']>): string =>
  t(`clients:recentActivities.descriptions.guest.fields.${field}`, { defaultValue: field })

const formatChangeValue = (
  field: string,
  value: GuestActivityValue,
  timezone: string | undefined,
  t: TFunction<['clients']>,
): string => {
  if (value === null || value === '') return EMPTY_VALUE
  if (typeof value === 'boolean') {
    return t(`clients:recentActivities.descriptions.guest.${value ? 'yes' : 'no'}`)
  }
  // Note: the API returns startTime/endTime as ISO date-time strings.
  if (DATE_FIELDS.includes(field) && !Number.isNaN(new Date(value).getTime())) {
    return formatDateOnly(new Date(value), timezone)
  }
  return String(value)
}

// Note: Figma names these two roles differently from the shared USER_ROLE_LABEL_MAP, so they are
// overridden here only; other screens keep the shared labels.
const getRoleLabel = (role: string, t: TFunction<['clients']>): string => {
  if (role === RESIDENT_ROLE) return t('clients:recentActivities.descriptions.guest.roleResident')
  if (role === FRONT_DESK_ROLE)
    return t('clients:recentActivities.descriptions.guest.roleFrontDesk')
  return getUserRoleLabel(role)
}

// Note: every guest action names its performer with their role, e.g. "John Carter (Property Manager)".
const formatPerformer = (activity: ClientActivity, t: TFunction<['clients']>): string => {
  const name = activity.performer?.name || EMPTY_VALUE
  const role = activity.performer?.role
  return role ? `${name} (${getRoleLabel(role, t)})` : name
}

// Note: returns null for check-ins (no `action`, or `checkedIn`) - those keep their own
// time-based wording in each design.
export const getGuestActionDescription = (
  activity: ClientActivity,
  t: TFunction<['clients']>,
  timezone?: string,
): string | null => {
  const { action } = activity
  if (!action || action === 'checkedIn') return null

  const name = activity.name ?? t('clients:recentActivities.descriptions.guest.fallbackName')
  const performer = formatPerformer(activity, t)

  switch (action) {
    case 'created':
      return t('clients:recentActivities.descriptions.guest.created', { name, performer })
    case 'updated': {
      // Note: one entry per update, however many fields changed.
      const changes = (activity.changes ?? [])
        .map(({ field, oldValue, newValue }) =>
          t('clients:recentActivities.descriptions.guest.change', {
            field: getFieldLabel(field, t),
            newValue: formatChangeValue(field, newValue, timezone, t),
            oldValue: formatChangeValue(field, oldValue, timezone, t),
          }),
        )
        .join('; ')
      return t('clients:recentActivities.descriptions.guest.updated', {
        changes,
        name,
        performer,
      })
    }
    case 'deleted':
      return t('clients:recentActivities.descriptions.guest.deleted', { name, performer })
    case 'suspended':
      return t('clients:recentActivities.descriptions.guest.suspended', { name, performer })
    case 'unsuspended':
      return t('clients:recentActivities.descriptions.guest.unsuspended', { name, performer })
    case 'banned':
      return t('clients:recentActivities.descriptions.guest.banned', { name, performer })
    case 'unbanned':
      return t('clients:recentActivities.descriptions.guest.unbanned', { name, performer })
  }
}

// Note: action entries carry the moment the action happened in `createdAt`; check-ins and the
// other categories keep the existing `updatedAt ?? createdAt`.
export const getActivityTimestamp = (activity: ClientActivity): number =>
  activity.action && activity.action !== 'checkedIn'
    ? activity.createdAt
    : (activity.updatedAt ?? activity.createdAt)
